import { ANALYTICS_EVENT_NAMES, ANALYTICS_FLOWS, ANALYTICS_TRACKED_COMPONENTS } from "../constants/analytics-constants";
import { TrackingComponent, TrackingFlow } from "../hooks/useAnalyticsTracking/useAnalyticsTracking";

export const getGrantPermissionsPayload = ({
    component = ANALYTICS_TRACKED_COMPONENTS.USER_SIDEBAR,
    email,
    flow,
    projectIds = [],
    projectNames = [],
    userGroupIds,
    userGroupNames,
    opalAccessGranted = undefined, // We should probably have more granular definitions for this event: "NOT_SHOWN", "DECLINED", "PRE_EXISTING_X", "ADMIN" , "USER"
    opalAccessGrantedViaProduct
}: {
    component?: TrackingComponent;
    email: string;
    flow: TrackingFlow;
    projectIds?: string[];
    projectNames?: string[];
    userGroupIds: string[];
    userGroupNames: string[];
    opalAccessGranted?: string;
    opalAccessGrantedViaProduct?: boolean;
}) => {
    const data = {
        affectedUserId: email,
        projectIds,
        projectNames,
        userGroupIds: userGroupIds,
        userGroupNames: userGroupNames,
        opalAccessGranted,
        opalAccessGrantedViaProduct
    };
    return {
        name: ANALYTICS_EVENT_NAMES.GRANT_PERMISSIONS,
        component,
        flow,
        data
    };
};

export const getCreateCustomGroupPayload = ({
    instanceIds,
    instanceNames,
    roleIds,
    roleNames
}: {
    instanceIds: string[];
    instanceNames: string[];
    roleIds: string[];
    roleNames: string[];
}) => {
    const data = {
        instanceIds,
        instanceNames,
        roleNames,
        roleIds
    };

    return {
        name: ANALYTICS_EVENT_NAMES.CREATE_CUSTOM_GROUP,
        flow: ANALYTICS_FLOWS.GROUP_ACCESS,
        component: ANALYTICS_TRACKED_COMPONENTS.CREATE_GROUP_SIDEBAR,
        data
    };
};

export const getRemoveUserPayload = ({
    component = ANALYTICS_TRACKED_COMPONENTS.USERS_TAB,
    email,
    flow
}: {
    component?: TrackingComponent;
    email: string;
    flow: TrackingFlow;
}) => {
    const data = {
        affectedUserId: email
    };
    return {
        name: ANALYTICS_EVENT_NAMES.REMOVE_USER,
        component,
        flow,
        data
    };
};

export const getRevokeUserPayload = ({
    component = ANALYTICS_TRACKED_COMPONENTS.USERS_TAB,
    email,
    flow
}: {
    component?: TrackingComponent;
    email: string;
    flow: TrackingFlow;
}) => {
    const data = {
        affectedUserId: email
    };
    return {
        name: ANALYTICS_EVENT_NAMES.REVOKE_PERMISSIONS,
        component,
        flow,
        data
    };
};

export const getBulkUserActionPayload = ({
    action,
    emails,
    failedCount = 0,
    flow,
    retry = false
}: {
    action: string;
    emails: string[];
    flow: TrackingFlow;
    failedCount?: number;
    retry?: boolean;
}) => {
    const data = {
        affectedUserIds: emails,
        failedCount,
        successfulCount: emails.length,
        retry
    };

    return {
        component: ANALYTICS_TRACKED_COMPONENTS.USER_TABLE as TrackingComponent,
        data,
        flow,
        name: action
    };
};

export const getCreateCustomRolePayload = ({
    component = ANALYTICS_TRACKED_COMPONENTS.CREATE_CUSTOM_ROLE_SIDEBAR,
    flow = ANALYTICS_FLOWS.ROLES_PAGE,
    productId,
    roleProductName,
    instanceIds,
    instanceNames,
    roleId,
    roleName
}: {
    component?: TrackingComponent;
    flow: TrackingFlow;
    productId: string;
    roleProductName: string;
    instanceIds: string[];
    instanceNames: string[];
    roleName: string;
    roleId: string;
}) => {
    const data = {
        productId: productId,
        roleProductName: roleProductName,
        instanceIds: instanceIds,
        instanceNames: instanceNames,
        roleId: roleId,
        roleName: roleName
    };
    return {
        name: ANALYTICS_EVENT_NAMES.CREATE_CUSTOM_ROLE,
        component,
        flow,
        data
    };
};

export const getCreateTeamPayload = ({
    flow = ANALYTICS_FLOWS.TEAMS,
    instanceId,
    productId,
    teamName,
    users = []
}: {
    flow?: TrackingFlow;
    instanceId: string;
    productId: string;
    teamName: string;
    users: string[];
}) => {
    const data = {
        name: teamName,
        users,
        productId,
        instanceId
    };
    return {
        name: ANALYTICS_EVENT_NAMES.CREATE_TEAM,
        component: ANALYTICS_TRACKED_COMPONENTS.TEAM_SIDEBAR,
        flow,
        data
    };
};

export const getEditTeamPayload = ({
    flow = ANALYTICS_FLOWS.TEAMS,
    instanceId,
    nameChanged,
    descriptionChanged,
    productId,
    usersAdded,
    usersRemoved
}: {
    flow?: TrackingFlow;
    instanceId: string;
    nameChanged: boolean;
    descriptionChanged: boolean;
    productId: string;
    usersAdded: string[];
    usersRemoved: string[];
}) => {
    const data = {
        instanceId,
        nameChanged,
        descriptionChanged,
        productId,
        usersAdded,
        usersRemoved
    };
    return {
        name: ANALYTICS_EVENT_NAMES.EDIT_TEAM,
        component: ANALYTICS_TRACKED_COMPONENTS.TEAM_SIDEBAR,
        flow,
        data
    };
};

export const getBillingNotificationsUpdatedPayload = ({
    component = ANALYTICS_TRACKED_COMPONENTS.NOTIFICATION_RECIPIENTS_SHEET,
    flow = ANALYTICS_FLOWS.SETTINGS,
    instanceId,
    notificationProductName,
    productId,
    usersAdded,
    usersRemoved
}: {
    component?: TrackingComponent;
    flow?: TrackingFlow;
    instanceId?: string;
    notificationProductName?: string;
    productId: string;
    usersAdded: string[];
    usersRemoved: string[];
}) => {
    const data = {
        ...(instanceId !== undefined && { instanceId }),
        ...(notificationProductName !== undefined && { notificationProductName }),
        productId,
        usersAdded,
        usersRemoved
    };
    return {
        name: ANALYTICS_EVENT_NAMES.BILLING_NOTIFICATIONS_UPDATED,
        component,
        flow,
        data
    };
};

export const getDeleteTeamPayload = ({
    flow = ANALYTICS_FLOWS.TEAMS,
    instanceId,
    productId
}: {
    flow?: TrackingFlow;
    instanceId: string;
    productId: string;
}) => {
    const data = {
        instanceId,
        productId
    };
    return {
        name: ANALYTICS_EVENT_NAMES.DELETE_TEAM,
        component: ANALYTICS_TRACKED_COMPONENTS.TEAM_SIDEBAR,
        flow,
        data
    };
};
